import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { firebaseService } from '../../services/firebaseService';
import { Search, Filter, MoreHorizontal, Plus, Download, Printer, Edit, Trash2, DollarSign, Calendar, Settings, User, ChevronUp, ChevronDown, Link2, X, Check, UserX } from 'lucide-react';
import { LinkPagamento } from '../../services/firebaseService';
import { ref, update } from 'firebase/database';
import { database } from '../../firebase/config';
import { useNotification } from '../../hooks/useNotification';

interface Cobranca {
  id: string;
  cliente: string;
  valor: number;
  descricao: string;
  formaPagamento: string;
  dataVencimento: string;
  status: 'pendente' | 'pago' | 'vencido' | 'cancelado';
  tipo: 'avulsa' | 'assinatura';
  invoiceUrl?: string;
  anoMes?: string;
  alunoId?: string;
  dataPagamento?: string;
  valorPago?: number;
  clienteInativo?: boolean;
}

const Cobrancas: React.FC = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const { showSuccess, showError, showConfirm, NotificationComponent } = useNotification();
  const [cobrancas, setCobrancas] = useState<Cobranca[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showBatchActions, setShowBatchActions] = useState(false);
  const [sortField, setSortField] = useState<keyof Cobranca | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Estados dos filtros
  const [filtros, setFiltros] = useState({
    dataInicio: '',
    dataFim: '',
    tipos: [] as string[],
    situacoes: [] as string[]
  });

  // Estados para atribuição de links
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [links, setLinks] = useState<LinkPagamento[]>([]);
  const [selectedLinkId, setSelectedLinkId] = useState<string>('');
  const [selectedMensalidades, setSelectedMensalidades] = useState<Set<string>>(new Set());
  const [updatingLinks, setUpdatingLinks] = useState(false);
  
  // Filtros do modal de atribuição
  const [filtrosModal, setFiltrosModal] = useState({
    valorMinimo: '',
    valorMaximo: '',
    apenasSemLink: false,
    status: [] as string[],
    buscaCliente: ''
  });

  // Estados para modal de edição de mensalidade
  const [showEditModal, setShowEditModal] = useState(false);
  const [mensalidadeEditando, setMensalidadeEditando] = useState<Cobranca | null>(null);
  const [editForm, setEditForm] = useState({
    valor: '',
    dataVencimento: '',
    status: 'pendente',
    valorPago: '',
    dataPagamento: ''
  });
  const [saving, setSaving] = useState(false);

  // Estados para modal de receber pagamento
  const [showReceberModal, setShowReceberModal] = useState(false);
  const [cobrancaRecebendo, setCobrancaRecebendo] = useState<Cobranca | null>(null);
  const [receberForm, setReceberForm] = useState({
    formaPagamento: '',
    dataPagamento: '',
    valorPago: ''
  });
  const [recebendo, setRecebendo] = useState(false);

  // Função para verificar se uma cobrança está vencida
  const isCobrancaVencida = (dataVencimento: string, status: string): boolean => {
    if (status === 'pago' || status === 'cancelado') return false;
    
    try {
      // Converter data de vencimento para Date
      let dataVenc: Date;
      
      // Se a data está no formato DD/MM/YYYY
      if (dataVencimento.includes('/')) {
        const [dia, mes, ano] = dataVencimento.split('/');
        dataVenc = new Date(parseInt(ano), parseInt(mes) - 1, parseInt(dia));
      } else {
        // Se está no formato ISO ou YYYY-MM-DD
        dataVenc = new Date(dataVencimento);
      }
      
      // Data atual sem hora (apenas dia, mês, ano)
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      dataVenc.setHours(0, 0, 0, 0);
      
      // Verificar se está vencida (data de vencimento é anterior a hoje)
      return dataVenc < hoje;
    } catch (error) {
      console.error('Erro ao verificar se cobrança está vencida:', error, dataVencimento);
      return false;
    }
  };

  // Função para determinar o status correto da cobrança
  const getStatusCorreto = (dataVencimento: string, statusAtual: string): 'pendente' | 'pago' | 'vencido' | 'cancelado' => {
    if (statusAtual === 'pago' || statusAtual === 'cancelado') {
      return statusAtual as 'pago' | 'cancelado';
    }
    
    if (isCobrancaVencida(dataVencimento, statusAtual)) {
      return 'vencido';
    }
    
    return statusAtual as 'pendente' | 'vencido';
  };

  const loadCobrancas = React.useCallback(async () => {
    if (!userProfile?.empresaCnpj) return;
    
    try {
      setLoading(true);
      const [assinaturas, alunos, pagamentos] = await Promise.all([
        firebaseService.getAssinaturas(userProfile.empresaCnpj),
        firebaseService.getAllAlunos(userProfile.empresaCnpj),
        firebaseService.getPagamentos(userProfile.empresaCnpj)
      ]);

      const cobrancasData: Cobranca[] = [];
      const cobrancasParaAtualizar: Array<{id: string, tipo: 'avulsa' | 'assinatura', anoMes?: string, alunoId?: string, status: string}> = [];
      
      // Adicionar pagamentos avulsos
      pagamentos.forEach(pagamento => {
        const aluno = alunos.find(a => a.id === pagamento.alunoId);
        const statusCorreto = getStatusCorreto(pagamento.dataVencimento, pagamento.status);
        
        // Se o status mudou, precisamos atualizar no banco
        if (statusCorreto !== pagamento.status && statusCorreto === 'vencido') {
          cobrancasParaAtualizar.push({
            id: pagamento.id,
            tipo: 'avulsa',
            status: 'vencido'
          });
        }
        
        cobrancasData.push({
          id: pagamento.id,
          cliente: aluno?.nome || 'Cliente não encontrado',
          valor: pagamento.valor,
          descricao: pagamento.externalReference || 'Pagamento avulso',
          formaPagamento: pagamento.tipoPagamento,
          dataVencimento: pagamento.dataVencimento,
          status: statusCorreto,
          tipo: 'avulsa',
          dataPagamento: pagamento.dataPagamento,
          valorPago: pagamento.valorPago,
          clienteInativo: aluno ? !aluno.bAtivo : false
        });
      });
      
      // Adicionar mensalidades (cobranças de assinaturas)
      try {
        const mensalidades = await firebaseService.getAllMensalidadesComLocalizacao(userProfile.empresaCnpj);
        
        mensalidades.forEach(mensalidade => {
          const aluno = alunos.find(a => a.id === mensalidade.alunoId);
          const assinatura = assinaturas.find(a => a.id === mensalidade.assinaturaId);
          const statusCorreto = getStatusCorreto(mensalidade.dataVencimento, mensalidade.status);
          
          // Se o status mudou, precisamos atualizar no banco
          if (statusCorreto !== mensalidade.status && statusCorreto === 'vencido') {
            cobrancasParaAtualizar.push({
              id: mensalidade.id,
              tipo: 'assinatura',
              anoMes: mensalidade.anoMes,
              alunoId: mensalidade.alunoId,
              status: 'vencido'
            });
          }
          
          cobrancasData.push({
            id: mensalidade.id,
            cliente: aluno?.nome || 'Cliente não encontrado',
            valor: mensalidade.valor,
            descricao: `Mensalidade ${mensalidade.numero} - ${assinatura?.description || 'Assinatura'}`,
            formaPagamento: assinatura?.billingType || 'PIX',
            dataVencimento: mensalidade.dataVencimento,
            status: statusCorreto,
            tipo: 'assinatura',
            invoiceUrl: mensalidade.invoiceUrl,
            anoMes: mensalidade.anoMes,
            alunoId: mensalidade.alunoId,
            dataPagamento: mensalidade.dataPagamento,
            valorPago: mensalidade.valorPago,
            clienteInativo: aluno ? !aluno.bAtivo : false
          });
        });
      } catch (error) {
        console.error('Erro ao carregar mensalidades:', error);
      }

      // Atualizar status de cobranças vencidas no banco de dados
      if (cobrancasParaAtualizar.length > 0) {
        try {
          const updates: any = {};
          
          for (const cobranca of cobrancasParaAtualizar) {
            if (cobranca.tipo === 'avulsa') {
              const pagamento = pagamentos.find(p => p.id === cobranca.id);
              if (pagamento) {
                updates[`empresas/${userProfile.empresaCnpj}/pagamentos/${cobranca.id}/status`] = 'vencido';
                updates[`empresas/${userProfile.empresaCnpj}/pagamentos/${cobranca.id}/updateDate`] = new Date().toISOString();
              }
            } else {
              // Atualizar mensalidade
              if (cobranca.anoMes && cobranca.alunoId) {
                updates[`empresas/${userProfile.empresaCnpj}/mensalidades/${cobranca.anoMes}/${cobranca.alunoId}/${cobranca.id}/status`] = 'vencido';
                updates[`empresas/${userProfile.empresaCnpj}/mensalidades/${cobranca.anoMes}/${cobranca.alunoId}/${cobranca.id}/updateDate`] = new Date().toISOString();
              }
            }
          }
          
          if (Object.keys(updates).length > 0) {
            await update(ref(database), updates);
            console.log(`✅ ${cobrancasParaAtualizar.length} cobrança(s) atualizada(s) para vencida(s)`);
          }
        } catch (error) {
          console.error('Erro ao atualizar status de cobranças vencidas:', error);
        }
      }

      setCobrancas(cobrancasData);
    } catch (error) {
      console.error('Erro ao carregar cobranças:', error);
    } finally {
      setLoading(false);
    }
  }, [userProfile?.empresaCnpj]);

  useEffect(() => {
    loadCobrancas();
  }, [loadCobrancas]);

  useEffect(() => {
    const loadLinks = async () => {
      if (userProfile?.empresaCnpj) {
        try {
          const linksData = await firebaseService.getLinks(userProfile.empresaCnpj);
          setLinks(linksData);
        } catch (error) {
          console.error('Erro ao carregar links:', error);
        }
      }
    };
    loadLinks();
  }, [userProfile?.empresaCnpj]);

  const filteredCobrancas = cobrancas.filter(cobranca => {
    // Filtro por busca de texto
    const matchesSearch = cobranca.cliente.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         cobranca.descricao.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;
    
    // Filtro por período de vencimento
    if (filtros.dataInicio || filtros.dataFim) {
      // Converter data de vencimento para formato YYYY-MM-DD para comparação precisa
      let dataVencimentoStr = cobranca.dataVencimento;
      
      // Se a data está no formato DD/MM/YYYY, converter para YYYY-MM-DD
      if (dataVencimentoStr.includes('/')) {
        const [dia, mes, ano] = dataVencimentoStr.split('/');
        dataVencimentoStr = `${ano}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`;
      }
      
      // Aplicar filtro de data início (inclusive)
      if (filtros.dataInicio && dataVencimentoStr < filtros.dataInicio) {
        return false;
      }
      
      // Aplicar filtro de data fim (inclusive)
      if (filtros.dataFim && dataVencimentoStr > filtros.dataFim) {
        return false;
      }
    }
    
    // Filtro por tipos de cobrança
    if (filtros.tipos.length > 0) {
      const tipoCobranca = cobranca.tipo === 'avulsa' ? 'Avulsas' : 'Assinaturas';
      if (!filtros.tipos.includes(tipoCobranca)) return false;
    }
    
    // Filtro por situações
    if (filtros.situacoes.length > 0) {
      let situacao = '';
      switch (cobranca.status) {
        case 'pendente':
          situacao = 'Aguardando pagamento';
          break;
        case 'vencido':
          situacao = 'Vencida';
          break;
        case 'pago':
          situacao = 'Recebida';
          break;
        default:
          situacao = cobranca.status;
      }
      
      if (!filtros.situacoes.includes(situacao)) return false;
    }
    
    return true;
  });

  // Aplicar ordenação
  const sortedCobrancas = [...filteredCobrancas].sort((a, b) => {
    if (!sortField) return 0;
    
    const aValue = a[sortField];
    const bValue = b[sortField];
    
    // Tratamento especial para datas
    if (sortField === 'dataVencimento' || sortField === 'dataPagamento') {
      const parseDate = (dateStr: string | undefined) => {
        if (!dateStr) return null;
        // Se já está no formato DD/MM/YYYY, converter para Date
        if (dateStr.includes('/')) {
          const [dia, mes, ano] = dateStr.split('/');
          return new Date(parseInt(ano), parseInt(mes) - 1, parseInt(dia));
        }
        // Se está em formato ISO ou outro, usar Date normal
        return new Date(dateStr);
      };
      
      const aDate = parseDate(aValue as string | undefined);
      const bDate = parseDate(bValue as string | undefined);
      
      // Se uma das datas é null/undefined, tratar como menor
      if (!aDate && !bDate) return 0;
      if (!aDate) return 1; // aDate é null, vai para o final
      if (!bDate) return -1; // bDate é null, vai para o final
      
      // Verificar se as datas são válidas
      if (isNaN(aDate.getTime()) || isNaN(bDate.getTime())) {
        return 0;
      }
      
      const result = sortDirection === 'asc' ? aDate.getTime() - bDate.getTime() : bDate.getTime() - aDate.getTime();
      return result;
    }
    
    // Tratamento especial para valores numéricos
    if (sortField === 'valor') {
      return sortDirection === 'asc' ? (aValue as number) - (bValue as number) : (bValue as number) - (aValue as number);
    }
    
    // Tratamento para strings
    const aStr = String(aValue).toLowerCase();
    const bStr = String(bValue).toLowerCase();
    
    if (sortDirection === 'asc') {
      return aStr.localeCompare(bStr);
    } else {
      return bStr.localeCompare(aStr);
    }
  });


  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pago':
        return '✓';
      case 'vencido':
        return '⚠';
      case 'pendente':
        return '⏰';
      default:
        return '●';
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const handleReceberPagamento = (cobranca: Cobranca) => {
      if (cobranca.tipo === 'avulsa') {
      // Para pagamentos avulsos, manter comportamento original
      handleReceberPagamentoAvulso(cobranca);
    } else {
      // Para mensalidades, abrir modal para escolher forma de pagamento e data
      setCobrancaRecebendo(cobranca);
      setReceberForm({
        formaPagamento: cobranca.formaPagamento || 'PIX',
        dataPagamento: new Date().toISOString().split('T')[0],
        valorPago: cobranca.valor.toString()
      });
      setShowReceberModal(true);
    }
  };

  const handleReceberPagamentoAvulso = async (cobranca: Cobranca) => {
    try {
        const pagamentos = await firebaseService.getPagamentos(userProfile?.empresaCnpj || '');
        const pagamento = pagamentos.find(p => p.id === cobranca.id);
        if (pagamento) {
          await firebaseService.updatePagamentoStatus(userProfile?.empresaCnpj || '', pagamento, 'pago', cobranca.valor);
          showSuccess('Sucesso', `Pagamento de ${cobranca.cliente} marcado como recebido! Valor: ${formatCurrency(cobranca.valor)}`);
        loadCobrancas();
        }
    } catch (error) {
      console.error('Erro ao receber pagamento:', error);
      showError('Erro', 'Erro ao processar pagamento. Tente novamente.');
    }
  };

  const handleCloseReceberModal = () => {
    setShowReceberModal(false);
    setCobrancaRecebendo(null);
    setReceberForm({
      formaPagamento: '',
      dataPagamento: '',
      valorPago: ''
    });
  };

  const handleConfirmarRecebimento = async () => {
    if (!cobrancaRecebendo || !cobrancaRecebendo.anoMes || !cobrancaRecebendo.alunoId) {
      showError('Erro', 'Dados da mensalidade incompletos.');
      return;
    }

    if (!receberForm.formaPagamento || !receberForm.dataPagamento) {
      showError('Atenção', 'Por favor, preencha a forma de pagamento e a data de recebimento.');
      return;
    }

    setRecebendo(true);
    try {
      const valorPago = parseFloat(receberForm.valorPago) || cobrancaRecebendo.valor;
      
      // Converter data para formato ISO
      const dataPagamentoISO = new Date(receberForm.dataPagamento + 'T00:00:00').toISOString();

      await firebaseService.updateMensalidade(
        userProfile?.empresaCnpj || '',
        cobrancaRecebendo.id,
        cobrancaRecebendo.anoMes,
        cobrancaRecebendo.alunoId,
        {
          status: 'pago',
          valorPago: valorPago,
          dataPagamento: dataPagamentoISO,
          tipoPagamento: receberForm.formaPagamento
        }
      );

      showSuccess('Sucesso', `Mensalidade de ${cobrancaRecebendo.cliente} marcada como paga!\nValor: ${formatCurrency(valorPago)}\nForma: ${receberForm.formaPagamento}\nData: ${formatDate(receberForm.dataPagamento)}`);
      
      handleCloseReceberModal();
      loadCobrancas();
    } catch (error) {
      console.error('Erro ao receber pagamento:', error);
      showError('Erro', 'Erro ao processar pagamento. Tente novamente.');
    } finally {
      setRecebendo(false);
    }
  };

  const handleRenovarCobranca = async (cobranca: Cobranca) => {
    try {
      if (cobranca.tipo === 'avulsa') {
        // Renovar cobrança avulsa - criar nova cobrança com data futura
        const novaDataVencimento = new Date();
        novaDataVencimento.setDate(novaDataVencimento.getDate() + 30);
        
        const novoPagamento = {
          alunoId: cobranca.id, // Usar ID da cobrança como referência
          valor: cobranca.valor,
          dataVencimento: novaDataVencimento.toISOString().split('T')[0],
          descricao: `Renovação - ${cobranca.descricao}`,
          tipoPagamento: cobranca.formaPagamento,
          status: 'pendente'
        };
        
        await firebaseService.createPagamento(userProfile?.empresaCnpj || '', novoPagamento);
        showSuccess('Sucesso', `Cobrança avulsa de ${cobranca.cliente} renovada por mais 30 dias!`);
      } else {
        // Para mensalidades, atualizar data de vencimento para 30 dias à frente
        if (cobranca.anoMes && cobranca.alunoId) {
          const novaDataVencimento = new Date();
          novaDataVencimento.setDate(novaDataVencimento.getDate() + 30);
          const dataFormatada = novaDataVencimento.toISOString().split('T')[0];
          
          await firebaseService.updateMensalidade(
            userProfile?.empresaCnpj || '',
            cobranca.id,
            cobranca.anoMes,
            cobranca.alunoId,
            {
              dataVencimento: dataFormatada,
              status: 'pendente'
            }
          );
          showSuccess('Sucesso', `Mensalidade de ${cobranca.cliente} renovada por mais 30 dias!`);
        } else {
          showError('Erro', 'Dados da mensalidade incompletos.');
        }
      }
      loadCobrancas();
    } catch (error) {
      console.error('Erro ao renovar cobrança:', error);
      showError('Erro', 'Erro ao renovar cobrança. Tente novamente.');
    }
  };

  // Função auxiliar para criar elemento de forma segura
  const createSafeElement = (doc: Document, tag: string, className?: string, textContent?: string): HTMLElement => {
    const element = doc.createElement(tag);
    if (className) element.className = className;
    if (textContent) element.textContent = textContent; // textContent é seguro, previne XSS
    return element;
  };

  // Função auxiliar para criar campo de formulário de forma segura
  const createField = (doc: Document, label: string, value: string): HTMLElement => {
    const field = createSafeElement(doc, 'div', 'field');
    const labelSpan = createSafeElement(doc, 'span', 'label', label);
    const valueSpan = createSafeElement(doc, 'span', 'value', value);
    field.appendChild(labelSpan);
    field.appendChild(valueSpan);
    return field;
  };

  const handleGerarNotaFiscal = (cobranca: Cobranca) => {
    // Gerar nota fiscal em nova janela usando manipulação segura do DOM
    const nfWindow = window.open('', '_blank');
    if (!nfWindow) return;

    const doc = nfWindow.document;
    
    // Criar elementos HTML de forma segura
    const html = doc.createElement('html');
    const head = doc.createElement('head');
    const body = doc.createElement('body');
    
    // Título
    const title = doc.createElement('title');
    title.textContent = `Nota Fiscal - ${cobranca.cliente}`;
    head.appendChild(title);
    
    // Estilos
    const style = doc.createElement('style');
    style.textContent = `
      body { font-family: Arial, sans-serif; margin: 20px; }
      .nf { border: 1px solid #000; padding: 20px; max-width: 600px; }
      .header { text-align: center; font-weight: bold; margin-bottom: 20px; border-bottom: 1px solid #000; padding-bottom: 10px; }
      .field { margin: 10px 0; display: flex; }
      .label { font-weight: bold; width: 150px; }
      .value { margin-left: 10px; }
      .total { border-top: 2px solid #000; margin-top: 20px; padding-top: 10px; font-weight: bold; font-size: 18px; }
    `;
    head.appendChild(style);
    
    // Conteúdo
    const nfDiv = createSafeElement(doc, 'div', 'nf');
    const header = createSafeElement(doc, 'div', 'header', 'NOTA FISCAL');
    nfDiv.appendChild(header);
    
    // Campos usando textContent para prevenir XSS
    nfDiv.appendChild(createField(doc, 'Cliente:', cobranca.cliente || ''));
    nfDiv.appendChild(createField(doc, 'Serviço:', cobranca.descricao || ''));
    nfDiv.appendChild(createField(doc, 'Data de Vencimento:', formatDate(cobranca.dataVencimento)));
    nfDiv.appendChild(createField(doc, 'Forma de Pagamento:', cobranca.formaPagamento || ''));
    nfDiv.appendChild(createField(doc, 'Status:', cobranca.status || ''));
    
    // Total
    const totalDiv = createSafeElement(doc, 'div', 'total');
    const totalLabel = createSafeElement(doc, 'span', 'label', 'Valor Total:');
    const totalValue = createSafeElement(doc, 'span', 'value', formatCurrency(cobranca.valor));
    totalDiv.appendChild(totalLabel);
    totalDiv.appendChild(totalValue);
    nfDiv.appendChild(totalDiv);
    
    // Rodapé
    const footer = createSafeElement(doc, 'div');
    footer.style.marginTop = '30px';
    footer.style.textAlign = 'center';
    footer.style.fontSize = '12px';
    footer.style.color = '#666';
    footer.textContent = 'Esta é uma nota fiscal gerada automaticamente pelo sistema SwimmingPool';
    nfDiv.appendChild(footer);
    
    body.appendChild(nfDiv);
    html.appendChild(head);
    html.appendChild(body);
    doc.appendChild(html);
  };

  const handleEditarCobranca = (cobranca: Cobranca) => {
    if (cobranca.tipo === 'assinatura') {
      // Abrir modal de edição para mensalidade
      setMensalidadeEditando(cobranca);
      
      // Converter data de vencimento para formato YYYY-MM-DD se necessário
      let dataVencimento = cobranca.dataVencimento;
      if (dataVencimento.includes('/')) {
        const [dia, mes, ano] = dataVencimento.split('/');
        dataVencimento = `${ano}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`;
      }
      
      // Converter data de pagamento para formato YYYY-MM-DD se necessário
      let dataPagamento = '';
      if (cobranca.dataPagamento) {
        try {
          // Se está em formato ISO, extrair apenas a data
          if (cobranca.dataPagamento.includes('T')) {
            dataPagamento = cobranca.dataPagamento.split('T')[0];
          } else if (cobranca.dataPagamento.includes('/')) {
            // Se está no formato DD/MM/YYYY, converter para YYYY-MM-DD
            const [dia, mes, ano] = cobranca.dataPagamento.split('/');
            dataPagamento = `${ano}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`;
          } else {
            // Assumir que já está no formato YYYY-MM-DD
            dataPagamento = cobranca.dataPagamento;
          }
        } catch (error) {
          console.error('Erro ao converter data de pagamento:', error);
          dataPagamento = '';
        }
      }
      
      setEditForm({
        valor: cobranca.valor.toString(),
        dataVencimento: dataVencimento,
        status: cobranca.status,
        valorPago: cobranca.valorPago ? cobranca.valorPago.toString() : '',
        dataPagamento: dataPagamento
      });
      setShowEditModal(true);
    } else {
      // Para pagamentos avulsos, navegar para página de detalhes
    navigate(`/cobrancas/detalhes/${cobranca.id}`);
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setMensalidadeEditando(null);
    setEditForm({
      valor: '',
      dataVencimento: '',
      status: 'pendente',
      valorPago: '',
      dataPagamento: ''
    });
  };

  const handleSalvarEdicao = async () => {
    if (!mensalidadeEditando || !mensalidadeEditando.anoMes || !mensalidadeEditando.alunoId) {
      showError('Erro', 'Dados da mensalidade incompletos.');
      return;
    }

    setSaving(true);
    try {
      const dadosAtualizacao: any = {};
      
      if (editForm.valor) {
        dadosAtualizacao.valor = parseFloat(editForm.valor);
      }
      if (editForm.dataVencimento) {
        dadosAtualizacao.dataVencimento = editForm.dataVencimento;
      }
      
      // Atualizar status automaticamente para "pago" se valorPago e dataPagamento forem preenchidos
      if (editForm.valorPago && editForm.dataPagamento) {
        dadosAtualizacao.status = 'pago';
        dadosAtualizacao.valorPago = parseFloat(editForm.valorPago);
        // Converter dataPagamento para formato ISO se necessário
        if (editForm.dataPagamento.includes('T')) {
          dadosAtualizacao.dataPagamento = editForm.dataPagamento;
        } else {
          dadosAtualizacao.dataPagamento = new Date(editForm.dataPagamento + 'T00:00:00').toISOString();
        }
      } else {
        // Se não preencheu ambos, usar o status informado manualmente
        if (editForm.status) {
          dadosAtualizacao.status = editForm.status;
        }
        if (editForm.valorPago) {
          dadosAtualizacao.valorPago = parseFloat(editForm.valorPago);
        }
        if (editForm.dataPagamento) {
          // Converter dataPagamento para formato ISO se necessário
          if (editForm.dataPagamento.includes('T')) {
            dadosAtualizacao.dataPagamento = editForm.dataPagamento;
          } else {
            dadosAtualizacao.dataPagamento = new Date(editForm.dataPagamento + 'T00:00:00').toISOString();
          }
        } else if (editForm.status === 'pago' && !editForm.dataPagamento) {
          // Se marcou como pago mas não informou data, usar data atual
          dadosAtualizacao.dataPagamento = new Date().toISOString();
        }
      }

      await firebaseService.updateMensalidade(
        userProfile?.empresaCnpj || '',
        mensalidadeEditando.id,
        mensalidadeEditando.anoMes,
        mensalidadeEditando.alunoId,
        dadosAtualizacao
      );

      showSuccess('Sucesso', 'Mensalidade atualizada com sucesso!');
      handleCloseEditModal();
      loadCobrancas();
    } catch (error) {
      console.error('Erro ao salvar edição:', error);
      showError('Erro', 'Erro ao salvar alterações. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const handleImprimirBoleto = (cobranca: Cobranca) => {
    // Abrir janela de impressão com dados do boleto usando manipulação segura do DOM
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const doc = printWindow.document;
    
    // Criar elementos HTML de forma segura
    const html = doc.createElement('html');
    const head = doc.createElement('head');
    const body = doc.createElement('body');
    
    // Título
    const title = doc.createElement('title');
    title.textContent = `Boleto - ${cobranca.cliente}`;
    head.appendChild(title);
    
    // Estilos
    const style = doc.createElement('style');
    style.textContent = `
      body { font-family: Arial, sans-serif; margin: 20px; }
      .boleto { border: 2px solid #000; padding: 20px; max-width: 400px; }
      .header { text-align: center; font-weight: bold; margin-bottom: 20px; }
      .field { margin: 10px 0; }
      .label { font-weight: bold; }
      .value { margin-left: 10px; }
    `;
    head.appendChild(style);
    
    // Conteúdo
    const boletoDiv = createSafeElement(doc, 'div', 'boleto');
    const header = createSafeElement(doc, 'div', 'header', 'BOLETO BANCÁRIO');
    boletoDiv.appendChild(header);
    
    // Campos usando textContent para prevenir XSS
    boletoDiv.appendChild(createField(doc, 'Cliente:', cobranca.cliente || ''));
    boletoDiv.appendChild(createField(doc, 'Valor:', formatCurrency(cobranca.valor)));
    boletoDiv.appendChild(createField(doc, 'Descrição:', cobranca.descricao || ''));
    boletoDiv.appendChild(createField(doc, 'Vencimento:', formatDate(cobranca.dataVencimento)));
    boletoDiv.appendChild(createField(doc, 'Forma de Pagamento:', cobranca.formaPagamento || ''));
    boletoDiv.appendChild(createField(doc, 'Status:', cobranca.status || ''));
    
    body.appendChild(boletoDiv);
    html.appendChild(head);
    html.appendChild(body);
    doc.appendChild(html);
    printWindow?.print();
  };

  const handleEnviarEmail = (cobranca: Cobranca) => {
    // Gerar link de email com dados do boleto
    const assunto = `Boleto - ${cobranca.cliente}`;
    const corpo = `
Olá ${cobranca.cliente},

Segue o boleto para pagamento:

Valor: ${formatCurrency(cobranca.valor)}
Descrição: ${cobranca.descricao}
Vencimento: ${formatDate(cobranca.dataVencimento)}
Forma de Pagamento: ${cobranca.formaPagamento}

Para pagar, acesse o link: [Link do boleto]

Atenciosamente,
Equipe SwimmingPool
    `;
    
    // Abrir cliente de email
    const mailtoLink = `mailto:?subject=${encodeURIComponent(assunto)}&body=${encodeURIComponent(corpo)}`;
    window.open(mailtoLink);
  };

  const handleExcluirCobranca = async (cobranca: Cobranca) => {
    showConfirm(
      'Excluir Cobrança',
      `Tem certeza que deseja excluir a cobrança de ${cobranca.cliente}?`,
      async () => {
        try {
          console.log('🗑️ Excluindo cobrança:', cobranca);
          console.log('📋 Tipo:', cobranca.tipo);
          console.log('🆔 ID:', cobranca.id);
          
          if (cobranca.tipo === 'avulsa') {
            console.log('💳 Excluindo pagamento avulso...');
            await firebaseService.deletePagamento(userProfile?.empresaCnpj || '', cobranca.id);
          } else {
            console.log('📅 Excluindo mensalidade...');
            await firebaseService.deleteMensalidade(userProfile?.empresaCnpj || '', cobranca.id);
          }
          
          console.log('✅ Cobrança excluída com sucesso!');
          showSuccess('Sucesso', 'Cobrança excluída com sucesso!');
          loadCobrancas();
        } catch (error) {
          console.error('❌ Erro ao excluir cobrança:', error);
          showError('Erro', 'Erro ao excluir cobrança. Tente novamente.');
        }
      },
      {
        confirmText: 'Excluir',
        cancelText: 'Cancelar'
      }
    );
  };

  const formatDate = (dateString: string) => {
    try {
      // Se a data já está no formato DD/MM/YYYY, retornar como está
      if (dateString.includes('/')) {
        return dateString;
      }
      
      // Se está no formato ISO ou outro formato, converter
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        console.warn('Data inválida:', dateString);
        return 'Data inválida';
      }
      
      return date.toLocaleDateString('pt-BR');
    } catch (error) {
      console.error('Erro ao formatar data:', error, dateString);
      return 'Data inválida';
    }
  };

  const handleSort = (field: keyof Cobranca) => {
    console.log('🔄 Ordenando por:', field);
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: keyof Cobranca) => {
    if (sortField !== field) {
      return <ChevronUp className="h-4 w-4 text-gray-400 dark:text-gray-500" />;
    }
    return sortDirection === 'asc' ? 
      <ChevronUp className="h-4 w-4 text-primary-600" /> : 
      <ChevronDown className="h-4 w-4 text-primary-600" />;
  };

  const handleFiltroChange = (campo: string, valor: any) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  const handleTipoChange = (tipo: string, checked: boolean) => {
    setFiltros(prev => ({
      ...prev,
      tipos: checked 
        ? [...prev.tipos, tipo]
        : prev.tipos.filter(t => t !== tipo)
    }));
  };

  const handleSituacaoChange = (situacao: string, checked: boolean) => {
    setFiltros(prev => ({
      ...prev,
      situacoes: checked 
        ? [...prev.situacoes, situacao]
        : prev.situacoes.filter(s => s !== situacao)
    }));
  };

  const limparFiltros = () => {
    setFiltros({
      dataInicio: '',
      dataFim: '',
      tipos: [],
      situacoes: []
    });
  };

  const aplicarFiltros = () => {
    setShowFilters(false);
  };

  const hasFiltrosAtivos = () => {
    return !!(
      filtros.dataInicio ||
      filtros.dataFim ||
      filtros.tipos.length > 0 ||
      filtros.situacoes.length > 0
    );
  };

  const isMesAtualFiltrado = () => {
    if (!filtros.dataInicio || !filtros.dataFim) return false;
    
    const hoje = new Date();
    const primeiroDiaMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    const ultimoDiaMes = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
    
    const dataInicioMes = primeiroDiaMes.toISOString().split('T')[0];
    const dataFimMes = ultimoDiaMes.toISOString().split('T')[0];
    
    return filtros.dataInicio === dataInicioMes && filtros.dataFim === dataFimMes;
  };

  const isMesAnteriorFiltrado = () => {
    if (!filtros.dataInicio || !filtros.dataFim) return false;
    
    const hoje = new Date();
    const primeiroDiaMesAnterior = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
    const ultimoDiaMesAnterior = new Date(hoje.getFullYear(), hoje.getMonth(), 0);
    
    const dataInicioMesAnterior = primeiroDiaMesAnterior.toISOString().split('T')[0];
    const dataFimMesAnterior = ultimoDiaMesAnterior.toISOString().split('T')[0];
    
    return filtros.dataInicio === dataInicioMesAnterior && filtros.dataFim === dataFimMesAnterior;
  };

  const handleFiltroMesAtual = () => {
    const hoje = new Date();
    const primeiroDiaMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    const ultimoDiaMes = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
    
    const dataInicio = primeiroDiaMes.toISOString().split('T')[0];
    const dataFim = ultimoDiaMes.toISOString().split('T')[0];
    
    setFiltros({
      ...filtros,
      dataInicio: dataInicio,
      dataFim: dataFim
    });
    setShowFilters(false);
  };

  const handleFiltroMesAnterior = () => {
    const hoje = new Date();
    const primeiroDiaMesAnterior = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
    const ultimoDiaMesAnterior = new Date(hoje.getFullYear(), hoje.getMonth(), 0);
    
    const dataInicio = primeiroDiaMesAnterior.toISOString().split('T')[0];
    const dataFim = ultimoDiaMesAnterior.toISOString().split('T')[0];
    
    setFiltros({
      ...filtros,
      dataInicio: dataInicio,
      dataFim: dataFim
    });
    setShowFilters(false);
  };

  // Funções para atribuição de links
  const handleOpenLinkModal = () => {
    setSelectedMensalidades(new Set());
    setSelectedLinkId('');
    setFiltrosModal({
      valorMinimo: '',
      valorMaximo: '',
      apenasSemLink: false,
      status: [],
      buscaCliente: ''
    });
    setShowLinkModal(true);
  };

  const handleCloseLinkModal = () => {
    setShowLinkModal(false);
    setSelectedMensalidades(new Set());
    setSelectedLinkId('');
  };

  const handleToggleMensalidade = (cobrancaId: string) => {
    const newSelected = new Set(selectedMensalidades);
    if (newSelected.has(cobrancaId)) {
      newSelected.delete(cobrancaId);
    } else {
      newSelected.add(cobrancaId);
    }
    setSelectedMensalidades(newSelected);
  };

  const getMensalidadesFiltradas = () => {
    return sortedCobrancas.filter(c => {
      if (c.tipo !== 'assinatura') return false;
      
      // Filtro por busca de cliente
      if (filtrosModal.buscaCliente) {
        const busca = filtrosModal.buscaCliente.toLowerCase();
        if (!c.cliente.toLowerCase().includes(busca)) return false;
      }
      
      // Filtro por valor mínimo
      if (filtrosModal.valorMinimo) {
        const valorMin = parseFloat(filtrosModal.valorMinimo);
        if (isNaN(valorMin) || c.valor < valorMin) return false;
      }
      
      // Filtro por valor máximo
      if (filtrosModal.valorMaximo) {
        const valorMax = parseFloat(filtrosModal.valorMaximo);
        if (isNaN(valorMax) || c.valor > valorMax) return false;
      }
      
      // Filtro por apenas sem link
      if (filtrosModal.apenasSemLink && hasActiveLink(c)) return false;
      
      // Filtro por status
      if (filtrosModal.status.length > 0) {
        const statusMap: { [key: string]: string } = {
          'Aguardando pagamento': 'pendente',
          'Vencida': 'vencido',
          'Recebida': 'pago'
        };
        const statusCobranca = statusMap[c.status] || c.status;
        if (!filtrosModal.status.some(s => statusMap[s] === statusCobranca)) return false;
      }
      
      return true;
    });
  };

  const handleSelectAllMensalidades = () => {
    const mensalidadesFiltradas = getMensalidadesFiltradas();
    const idsFiltradas = mensalidadesFiltradas.map(c => c.id);
    
    if (selectedMensalidades.size === idsFiltradas.length && idsFiltradas.length > 0) {
      setSelectedMensalidades(new Set());
    } else {
      setSelectedMensalidades(new Set(idsFiltradas));
    }
  };

  const handleLimparFiltrosModal = () => {
    setFiltrosModal({
      valorMinimo: '',
      valorMaximo: '',
      apenasSemLink: false,
      status: [],
      buscaCliente: ''
    });
  };

  const handleStatusChangeModal = (status: string, checked: boolean) => {
    setFiltrosModal(prev => ({
      ...prev,
      status: checked 
        ? [...prev.status, status]
        : prev.status.filter(s => s !== status)
    }));
  };

  const handleAtribuirLinks = async () => {
    if (!selectedLinkId || selectedMensalidades.size === 0) {
      showError('Atenção', 'Por favor, selecione um link e pelo menos uma mensalidade.');
      return;
    }

    const selectedLink = links.find(l => l.id === selectedLinkId);
    if (!selectedLink) {
      showError('Erro', 'Link selecionado não encontrado.');
      return;
    }

    showConfirm(
      'Atribuir Link',
      `Deseja atribuir o link "${selectedLink.nome}" a ${selectedMensalidades.size} mensalidade(s)?`,
      async () => {
        setUpdatingLinks(true);
        try {
          const mensalidadesParaAtualizar = sortedCobrancas
            .filter(c => c.tipo === 'assinatura' && selectedMensalidades.has(c.id))
            .filter(c => c.anoMes && c.alunoId)
            .map(c => ({
              id: c.id,
              anoMes: c.anoMes!,
              alunoId: c.alunoId!
            }));

          if (mensalidadesParaAtualizar.length === 0) {
            showError('Atenção', 'Nenhuma mensalidade válida selecionada.');
            setUpdatingLinks(false);
            return;
          }

          const resultado = await firebaseService.updateMensalidadesInvoiceUrlBatch(
            userProfile?.empresaCnpj || '',
            mensalidadesParaAtualizar,
            selectedLink.url
          );

          showSuccess('Sucesso', `${resultado.sucesso} mensalidade(s) atualizada(s) com sucesso!${resultado.erro > 0 ? ` ${resultado.erro} erro(s).` : ''}`);
          
          handleCloseLinkModal();
          loadCobrancas();
        } catch (error) {
          console.error('Erro ao atribuir links:', error);
          showError('Erro', 'Erro ao atribuir links. Tente novamente.');
        } finally {
          setUpdatingLinks(false);
        }
      },
      {
        confirmText: 'Confirmar',
        cancelText: 'Cancelar'
      }
    );
  };

  const hasActiveLink = (cobranca: Cobranca) => {
    return cobranca.invoiceUrl && cobranca.invoiceUrl.trim() !== '';
  };

  const totalValue = sortedCobrancas.reduce((sum, cobranca) => sum + cobranca.valor, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li>
                <span className="text-gray-500">Cobranças</span>
              </li>
              <li>
                <span className="text-gray-400 dark:text-gray-500">/</span>
              </li>
              <li>
                <span className="text-gray-900 dark:text-gray-100 dark:text-gray-100 font-medium">Todas</span>
              </li>
            </ol>
          </nav>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 dark:text-gray-100 mt-2">Todas</h1>
        </div>
      </div>

      {/* Summary */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <span className="font-medium">{filteredCobrancas.length}</span> cobranças no valor total de{' '}
          <span className="font-medium">{formatCurrency(totalValue)}</span> das{' '}
          <span className="font-medium">{cobrancas.length}</span> cobranças existentes
        </div>
      </div>

      {/* Search and Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 h-4 w-4" />
            <input
              type="text"
              placeholder="Procurar por nome ou email do cliente"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-80"
            />
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
              hasFiltrosAtivos()
                ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/50'
                : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
            }`}
          >
            <Filter className="h-4 w-4" />
            <span>Filtros</span>
            {hasFiltrosAtivos() && (
              <span className="ml-1 h-2 w-2 rounded-full bg-green-600"></span>
            )}
          </button>

          <button
            onClick={() => handleFiltroMesAtual()}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
              isMesAtualFiltrado()
                ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/50'
                : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
            }`}
          >
            <Calendar className="h-4 w-4" />
            <span>Mês Atual</span>
            {isMesAtualFiltrado() && (
              <span className="ml-1 h-2 w-2 rounded-full bg-green-600"></span>
            )}
          </button>

          <button
            onClick={() => handleFiltroMesAnterior()}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
              isMesAnteriorFiltrado()
                ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/50'
                : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
            }`}
          >
            <Calendar className="h-4 w-4" />
            <span>Mês Anterior</span>
            {isMesAnteriorFiltrado() && (
              <span className="ml-1 h-2 w-2 rounded-full bg-green-600"></span>
            )}
          </button>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={handleOpenLinkModal}
            className="flex items-center space-x-2 px-4 py-2 border border-primary-300 text-primary-600 rounded-md hover:bg-primary-50"
          >
            <Link2 className="h-4 w-4" />
            <span>Atribuir Links</span>
          </button>
          
          <div className="relative">
            <button
              onClick={() => setShowBatchActions(!showBatchActions)}
              className="flex items-center space-x-2 px-4 py-2 border border-primary-300 text-primary-600 rounded-md hover:bg-primary-50"
            >
              <MoreHorizontal className="h-4 w-4" />
              <span>Ações em lote</span>
            </button>
            
            {showBatchActions && (
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-10">
                <div className="py-1">
                  <button className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                    <Download className="h-4 w-4" />
                    <span>Exportar dados</span>
                  </button>
                  <button className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                    <Printer className="h-4 w-4" />
                    <span>Imprimir boletos</span>
                  </button>
                  <button className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                    <Download className="h-4 w-4" />
                    <span>Baixar boletos</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          <button 
            onClick={() => {
              // Mostrar opções de tipo de cobrança
              const tipo = window.prompt('Escolha o tipo de cobrança:\n1 - Pagamento Avulso\n2 - Assinatura\n\nDigite 1 ou 2:');
              if (tipo === '1') {
                window.location.href = '/cobrancas/avulsas?new=true';
              } else if (tipo === '2') {
                window.location.href = '/cobrancas/assinaturas?new=true';
              }
            }}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            <Plus className="h-4 w-4" />
            <span>Adicionar cobrança</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Período de vencimento
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="date"
                  value={filtros.dataInicio}
                  onChange={(e) => handleFiltroChange('dataInicio', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <span className="text-gray-500">até</span>
                <input
                  type="date"
                  value={filtros.dataFim}
                  onChange={(e) => handleFiltroChange('dataFim', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Tipos de cobrança
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input 
                    type="checkbox" 
                    checked={filtros.tipos.includes('Avulsas')}
                    onChange={(e) => handleTipoChange('Avulsas', e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" 
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Avulsas</span>
                </label>
                <label className="flex items-center">
                  <input 
                    type="checkbox" 
                    checked={filtros.tipos.includes('Assinaturas')}
                    onChange={(e) => handleTipoChange('Assinaturas', e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" 
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Assinaturas</span>
                </label>
                <label className="flex items-center">
                  <input 
                    type="checkbox" 
                    checked={filtros.tipos.includes('Parceladas')}
                    onChange={(e) => handleTipoChange('Parceladas', e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" 
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Parceladas</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Situações das cobranças
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input 
                    type="checkbox" 
                    checked={filtros.situacoes.includes('Aguardando pagamento')}
                    onChange={(e) => handleSituacaoChange('Aguardando pagamento', e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" 
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Aguardando pagamento</span>
                </label>
                <label className="flex items-center">
                  <input 
                    type="checkbox" 
                    checked={filtros.situacoes.includes('Vencida')}
                    onChange={(e) => handleSituacaoChange('Vencida', e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" 
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Vencida</span>
                </label>
                <label className="flex items-center">
                  <input 
                    type="checkbox" 
                    checked={filtros.situacoes.includes('Recebida')}
                    onChange={(e) => handleSituacaoChange('Recebida', e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" 
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Recebida</span>
                </label>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button 
              onClick={limparFiltros}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Limpar
            </button>
            <button 
              onClick={aplicarFiltros}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
            >
              Aplicar
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th 
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('cliente')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Nome</span>
                        {getSortIcon('cliente')}
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('valor')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Valor</span>
                        {getSortIcon('valor')}
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Status do Pagamento</span>
                        {getSortIcon('status')}
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('formaPagamento')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Forma de pagamento</span>
                        {getSortIcon('formaPagamento')}
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('dataVencimento')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Data de vencimento</span>
                        {getSortIcon('dataVencimento')}
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('dataPagamento')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Data de recebimento</span>
                        {getSortIcon('dataPagamento')}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Link de Pagamento
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {sortedCobrancas.map((cobranca) => (
                <tr key={cobranca.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center mr-3 ${
                        cobranca.clienteInativo ? 'bg-gray-200 dark:bg-gray-600' : 'bg-green-100'
                      }`}>
                        <span className={`text-sm font-medium ${
                          cobranca.clienteInativo ? 'text-gray-500 dark:text-gray-400' : 'text-green-600'
                        }`}>
                          {cobranca.cliente.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 dark:text-gray-100">{cobranca.cliente}</div>
                        {cobranca.clienteInativo && (
                          <div title="Cliente inativo">
                            <UserX 
                              className="h-4 w-4 text-gray-400 dark:text-gray-500" 
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100 dark:text-gray-100">{formatCurrency(cobranca.valor)}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      cobranca.status === 'pago' 
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' 
                        : cobranca.status === 'pendente'
                        ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                        : cobranca.status === 'vencido'
                        ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                    }`}>
                      {cobranca.status === 'pago' ? 'Pago' : 
                       cobranca.status === 'pendente' ? 'Pendente' :
                       cobranca.status === 'vencido' ? 'Vencido' : 
                       cobranca.status}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-gray-100 dark:text-gray-100">{cobranca.formaPagamento}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900 dark:text-gray-100 dark:text-gray-100">{formatDate(cobranca.dataVencimento)}</span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900 dark:text-gray-100 dark:text-gray-100">
                      {cobranca.dataPagamento ? formatDate(cobranca.dataPagamento) : '-'}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    {cobranca.tipo === 'assinatura' ? (
                      <div className="flex items-center space-x-2">
                        {hasActiveLink(cobranca) ? (
                          <>
                            <Check className="h-4 w-4 text-green-600" />
                            <span className="text-sm text-green-600">Ativo</span>
                            {cobranca.invoiceUrl && (
                              <a
                                href={cobranca.invoiceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary-600 hover:text-primary-800"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Link2 className="h-4 w-4" />
                              </a>
                            )}
                          </>
                        ) : (
                          <>
                            <X className="h-4 w-4 text-red-600" />
                            <span className="text-sm text-red-600">Sem link</span>
                          </>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button 
                        onClick={() => handleEditarCobranca(cobranca)}
                        className="text-primary-600 hover:text-primary-900"
                        title="Editar cobrança"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={() => handleExcluirCobranca(cobranca)}
                        className="text-red-600 hover:text-red-900"
                        title="Excluir cobrança"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filteredCobrancas.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-500 text-lg">Nenhuma cobrança encontrada</div>
          <div className="text-gray-400 text-sm mt-2">
            {searchTerm ? 'Tente ajustar os filtros de busca' : 'Comece criando sua primeira cobrança'}
          </div>
        </div>
      )}

      {/* Modal de Atribuição de Links */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 dark:text-gray-100">Atribuir Links de Pagamento</h2>
              <button
                onClick={handleCloseLinkModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Seleção de Link */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Selecione o Link de Pagamento
                </label>
                <select
                  value={selectedLinkId}
                  onChange={(e) => setSelectedLinkId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Selecione um link...</option>
                  {links.map(link => (
                    <option key={link.id} value={link.id}>
                      {link.nome} - {formatCurrency(link.valor)} - {link.url.substring(0, 50)}...
                    </option>
                  ))}
                </select>
                {links.length === 0 && (
                  <p className="text-sm text-gray-500 mt-2">Nenhum link cadastrado. Cadastre links na seção de configurações.</p>
                )}
              </div>

              {/* Filtros do Modal */}
              <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Filtros</h3>
                  <button
                    onClick={handleLimparFiltrosModal}
                    className="text-xs text-primary-600 hover:text-primary-800"
                  >
                    Limpar filtros
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Busca por Cliente */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Buscar Cliente
                    </label>
                    <input
                      type="text"
                      placeholder="Nome do cliente..."
                      value={filtrosModal.buscaCliente}
                      onChange={(e) => setFiltrosModal(prev => ({ ...prev, buscaCliente: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Valor Mínimo */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Valor Mínimo
                    </label>
                    <input
                      type="number"
                      placeholder="R$ 0,00"
                      value={filtrosModal.valorMinimo}
                      onChange={(e) => setFiltrosModal(prev => ({ ...prev, valorMinimo: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      step="0.01"
                      min="0"
                    />
                  </div>

                  {/* Valor Máximo */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Valor Máximo
                    </label>
                    <input
                      type="number"
                      placeholder="R$ 0,00"
                      value={filtrosModal.valorMaximo}
                      onChange={(e) => setFiltrosModal(prev => ({ ...prev, valorMaximo: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      step="0.01"
                      min="0"
                    />
                  </div>

                  {/* Apenas sem link */}
                  <div className="flex items-end">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filtrosModal.apenasSemLink}
                        onChange={(e) => setFiltrosModal(prev => ({ ...prev, apenasSemLink: e.target.checked }))}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-xs text-gray-700">Apenas sem link</span>
                    </label>
                  </div>
                </div>

                {/* Filtro por Status */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">
                    Status
                  </label>
                  <div className="flex flex-wrap gap-3">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filtrosModal.status.includes('Aguardando pagamento')}
                        onChange={(e) => handleStatusChangeModal('Aguardando pagamento', e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-xs text-gray-700">Aguardando pagamento</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filtrosModal.status.includes('Vencida')}
                        onChange={(e) => handleStatusChangeModal('Vencida', e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-xs text-gray-700">Vencida</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filtrosModal.status.includes('Recebida')}
                        onChange={(e) => handleStatusChangeModal('Recebida', e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-xs text-gray-700">Recebida</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Lista de Mensalidades */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Mensalidades ({getMensalidadesFiltradas().length} de {sortedCobrancas.filter(c => c.tipo === 'assinatura').length} disponíveis)
                  </label>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={handleSelectAllMensalidades}
                      className="text-sm text-primary-600 hover:text-primary-800"
                    >
                      {selectedMensalidades.size === getMensalidadesFiltradas().length && getMensalidadesFiltradas().length > 0
                        ? 'Desmarcar todas'
                        : 'Selecionar todas filtradas'}
                    </button>
                  </div>
                </div>
                
                <div className="border border-gray-200 rounded-md max-h-96 overflow-y-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                          <input
                            type="checkbox"
                            checked={selectedMensalidades.size === sortedCobrancas.filter(c => c.tipo === 'assinatura').length && sortedCobrancas.filter(c => c.tipo === 'assinatura').length > 0}
                            onChange={handleSelectAllMensalidades}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Cliente
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Valor
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Vencimento
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Status Link
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {getMensalidadesFiltradas().map((cobranca) => (
                          <tr key={cobranca.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                checked={selectedMensalidades.has(cobranca.id)}
                                onChange={() => handleToggleMensalidade(cobranca.id)}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{cobranca.cliente}</td>
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{formatCurrency(cobranca.valor)}</td>
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{formatDate(cobranca.dataVencimento)}</td>
                            <td className="px-4 py-3">
                              {hasActiveLink(cobranca) ? (
                                <span className="inline-flex items-center space-x-1 text-green-600">
                                  <Check className="h-4 w-4" />
                                  <span className="text-xs">Ativo</span>
                                </span>
                              ) : (
                                <span className="inline-flex items-center space-x-1 text-red-600">
                                  <X className="h-4 w-4" />
                                  <span className="text-xs">Sem link</span>
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
                {getMensalidadesFiltradas().length === 0 && (
                  <p className="text-sm text-gray-500 mt-2 text-center py-4">
                    {sortedCobrancas.filter(c => c.tipo === 'assinatura').length === 0
                      ? 'Nenhuma mensalidade encontrada.'
                      : 'Nenhuma mensalidade corresponde aos filtros selecionados.'}
                  </p>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={handleCloseLinkModal}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={updatingLinks}
              >
                Cancelar
              </button>
              <button
                onClick={handleAtribuirLinks}
                disabled={!selectedLinkId || selectedMensalidades.size === 0 || updatingLinks}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {updatingLinks ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Atribuindo...</span>
                  </>
                ) : (
                  <>
                    <Link2 className="h-4 w-4" />
                    <span>Atribuir ({selectedMensalidades.size})</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edição de Mensalidade */}
      {showEditModal && mensalidadeEditando && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Editar Mensalidade</h2>
              <button
                onClick={handleCloseEditModal}
                className="text-gray-400 hover:text-gray-600"
                disabled={saving}
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Cliente:</span> {mensalidadeEditando.cliente}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Descrição:</span> {mensalidadeEditando.descricao}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Valor */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Valor
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editForm.valor}
                    onChange={(e) => setEditForm(prev => ({ ...prev, valor: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="0.00"
                  />
                </div>

                {/* Data de Vencimento */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Data de Vencimento
                  </label>
                  <input
                    type="date"
                    value={editForm.dataVencimento}
                    onChange={(e) => setEditForm(prev => ({ ...prev, dataVencimento: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Status
                  </label>
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="pendente">Pendente</option>
                    <option value="pago">Pago</option>
                    <option value="vencido">Vencido</option>
                  </select>
                </div>

                {/* Valor Pago */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Valor Pago
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editForm.valorPago}
                    onChange={(e) => setEditForm(prev => ({ ...prev, valorPago: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="0.00"
                  />
                </div>

                {/* Data de Pagamento */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Data de Pagamento
                  </label>
                  <input
                    type="date"
                    value={editForm.dataPagamento}
                    onChange={(e) => setEditForm(prev => ({ ...prev, dataPagamento: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  {editForm.status === 'pago' && !editForm.dataPagamento && (
                    <p className="text-xs text-gray-500 mt-1">Será preenchida automaticamente se não informada</p>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={handleCloseEditModal}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={saving}
              >
                Cancelar
              </button>
              <button
                onClick={handleSalvarEdicao}
                disabled={saving}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Salvando...</span>
                  </>
                ) : (
                  <span>Salvar</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Receber Pagamento */}
      {showReceberModal && cobrancaRecebendo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Receber Pagamento</h2>
              <button
                onClick={handleCloseReceberModal}
                className="text-gray-400 hover:text-gray-600"
                disabled={recebendo}
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <div className="mb-4 bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">
                  <span className="font-medium">Cliente:</span> {cobrancaRecebendo.cliente}
                </p>
                <p className="text-sm text-gray-600 mb-1">
                  <span className="font-medium">Descrição:</span> {cobrancaRecebendo.descricao}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Valor da Mensalidade:</span> {formatCurrency(cobrancaRecebendo.valor)}
                </p>
              </div>

              {/* Forma de Pagamento */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Forma de Pagamento <span className="text-red-500">*</span>
                </label>
                <select
                  value={receberForm.formaPagamento}
                  onChange={(e) => setReceberForm(prev => ({ ...prev, formaPagamento: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  disabled={recebendo}
                >
                  <option value="">Selecione...</option>
                  <option value="PIX">PIX</option>
                  <option value="CREDIT_CARD">Cartão de Crédito</option>
                  <option value="DEBIT_CARD">Cartão de Débito</option>
                  <option value="BOLETO">Boleto</option>
                  <option value="Dinheiro">Dinheiro</option>
                  <option value="Transferência">Transferência Bancária</option>
                  <option value="Outro">Outro</option>
                </select>
              </div>

              {/* Data de Recebimento */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Data de Recebimento <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={receberForm.dataPagamento}
                  onChange={(e) => setReceberForm(prev => ({ ...prev, dataPagamento: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  disabled={recebendo}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>

              {/* Valor Pago */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Valor Pago
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={receberForm.valorPago}
                  onChange={(e) => setReceberForm(prev => ({ ...prev, valorPago: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder={formatCurrency(cobrancaRecebendo.valor)}
                  disabled={recebendo}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Deixe em branco para usar o valor completo da mensalidade
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={handleCloseReceberModal}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={recebendo}
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmarRecebimento}
                disabled={recebendo || !receberForm.formaPagamento || !receberForm.dataPagamento}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {recebendo ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Processando...</span>
                  </>
                ) : (
                  <>
                    <DollarSign className="h-4 w-4" />
                    <span>Confirmar Recebimento</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <NotificationComponent />
    </div>
  );
};

export default Cobrancas;
